/**
 * <henk-variant-switcher> — variant switcher op de PDP met Section Rendering.
 *
 * Liquid-tegenhanger van Hydrogen's <ProductForm>: daar navigeert een swatch
 * naar `?${variantUriQuery}` (replace, preventScrollReset) en rendert de route
 * opnieuw. Hier doen we hetzelfde met de Section Rendering API: we zetten
 * `?variant=<id>` in de URL en laten Shopify de betrokken secties opnieuw
 * renderen, zodat prijs, galerij, preview én het verborgen variant-id
 * server-side kloppen in plaats van client-side bijgewerkt te worden.
 *
 * Markup-contract:
 *   <henk-variant-switcher
 *     data-section-id="{{ section.id }}"           de eigen sectie
 *     data-product-url="{{ product.url }}"         basis-URL voor ?variant=
 *     data-render-selector="[data-pdp-section]">   extra secties die mee moeten
 *     <div data-section-render="{{ section.id }}">
 *       ... swatches met [data-fabric-code] / [data-frame-code],
 *           .is-selected wordt server-side gezet ...
 *       <script type="application/json" class="configurator__variants">…</script>
 *     </div>
 *   </henk-variant-switcher>
 *
 * De swap vervangt alleen de inhoud van het binnenste element; dit custom
 * element en zijn gedelegeerde listener blijven bestaan.
 *
 * UI-state die de swap moet overleven (welke accordeon-stap open staat) hangt
 * aan `data-preserve-open="<key>"` en wordt door de section renderer meegenomen
 * — bewust dáár, want die zet de stand in de bron-HTML vóór de swap, zodat de
 * open-animatie niet opnieuw afspeelt.
 */
class HenkVariantSwitcher extends HTMLElement {
  connectedCallback() {
    this.sequence = 0;
    this.addEventListener("click", (event) => {
      const button = event.target.closest(
        "[data-fabric-code], [data-frame-code]",
      );
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      this.select(button);
    });
  }

  get variants() {
    const node = this.querySelector(".configurator__variants");
    if (!node) return [];
    try {
      return JSON.parse(node.textContent) || [];
    } catch (error) {
      console.error(
        "[henk-variant-switcher] kan variants JSON niet lezen",
        error,
      );
      return [];
    }
  }

  get selectedFabric() {
    return (
      this.querySelector("[data-fabric-code].is-selected")?.dataset
        .fabricCode || null
    );
  }

  get selectedFrame() {
    return (
      this.querySelector("[data-frame-code].is-selected")?.dataset.frameCode ||
      null
    );
  }

  get hasFrameOptions() {
    return Boolean(this.querySelector("[data-frame-code]"));
  }

  select(button) {
    const isFabric = "fabricCode" in button.dataset;
    const group = isFabric ? "[data-fabric-code]" : "[data-frame-code]";

    // Optimistische selectie, zodat de swatch direct reageert en de daarop
    // volgende getters de nieuwe keuze zien.
    this.querySelectorAll(group).forEach((el) => {
      el.classList.toggle("is-selected", el === button);
      el.setAttribute("aria-pressed", el === button ? "true" : "false");
    });

    const fabric = this.selectedFabric;
    const frame = this.selectedFrame;
    if (!fabric) return;
    if (this.hasFrameOptions && !frame) return;

    const variant = this.variants.find(
      (v) =>
        v.fabric === fabric && (!this.hasFrameOptions || v.frame === frame),
    );

    if (!variant) {
      console.warn("[henk-variant-switcher] geen variant voor", {
        fabric,
        frame,
      });
      return;
    }

    this.applyOptimistic(variant);
    this.render(variant.id);
  }

  /** Directe feedback vóór de server-render binnen is. */
  applyOptimistic(variant) {
    const input = this.querySelector('input[name="id"]');
    if (input) input.value = variant.id;

    const price = this.querySelector("[data-variant-price]");
    if (price && variant.price_formatted)
      price.textContent = variant.price_formatted;

    const preview = this.querySelector(".sofa-preview-image-wrapper");
    if (preview && variant.image) {
      const img = preview.querySelector("img");
      if (img) {
        img.src = variant.image;
        img.removeAttribute("srcset");
        img.removeAttribute("data-srcset");
      }
      preview.querySelectorAll("source").forEach((source) => {
        source.srcset = variant.image;
        if (source.hasAttribute("data-srcset"))
          source.setAttribute("data-srcset", variant.image);
      });
    }

    this.trackPreviewImage();
  }

  /**
   * Loading-state op de preview zolang de variant-afbeelding onderweg is.
   *
   * Dit staat los van `[data-loading]` op het custom element zelf: dat dekt
   * alleen het ophalen van de sectie-HTML, terwijl de afbeelding daar meestal
   * ná binnenkomt. Twee momenten zetten hem aan — de optimistische src-wissel
   * bij de klik, en de swap erna (dan is de <img> een vers element) — en het
   * `load`/`error`-event van díe <img> zet hem weer uit.
   *
   * `img.complete` vangt de cache-hit af, en de drempel van 120ms de rest: na
   * de swap is de <img> een vers element met dezelfde URL die de optimistische
   * wissel net geladen heeft, dus `complete` staat dan heel even op false. Direct
   * tonen geeft daar een spinner-flits; wachten tot het écht traag is niet.
   */
  trackPreviewImage() {
    const preview = this.querySelector(".sofa-preview-image-wrapper");
    if (!preview) return;

    clearTimeout(this.previewTimer);

    const img = preview.querySelector("img");
    if (!img || img.complete) {
      preview.removeAttribute("data-image-loading");
      return;
    }

    this.previewTimer = setTimeout(
      () => preview.setAttribute("data-image-loading", ""),
      120,
    );

    const done = () => {
      clearTimeout(this.previewTimer);
      preview.removeAttribute("data-image-loading");
    };
    img.addEventListener("load", done, { once: true });
    img.addEventListener("error", done, { once: true });
  }

  async render(variantId) {
    if (!window.HenkSections) return;

    const sectionId = this.dataset.sectionId;
    // De id's van de mee-te-renderen secties komen uit de DOM: in
    // JSON-templates krijgt een sectie een `template--<id>__`-prefix, dus
    // "main" hardcoden werkt niet.
    const extra = this.dataset.renderSelector
      ? window.HenkSections.ids(
          `${this.dataset.renderSelector}[${window.HenkSections.ATTR}]`,
        )
      : [];
    const sections = [...new Set([sectionId, ...extra])].filter(Boolean);

    const url = new URL(
      this.dataset.productUrl || window.location.pathname,
      window.location.origin,
    );
    url.searchParams.set("variant", variantId);

    // Hydrogen gebruikt `replace` zodat de back-knop niet volloopt met
    // variant-wissels; hier doet replaceState hetzelfde.
    window.history.replaceState({}, "", url.toString());

    const ticket = ++this.sequence;

    this.setAttribute("data-loading", "");
    try {
      const sectionsHtml = await window.HenkSections.render(
        sections,
        url.toString(),
      );
      // Een nieuwere klik heeft ons ingehaald: die render wint — die roept
      // trackPreviewImage() straks zelf aan op zijn eigen verse <img>.
      if (ticket !== this.sequence) return sectionsHtml;
      // De swap heeft de <img> vervangen; de listeners van hierboven zijn met
      // het oude element verdwenen, dus opnieuw meten op het nieuwe.
      this.trackPreviewImage();
      this.dispatchEvent(
        new CustomEvent("variant:changed", {
          bubbles: true,
          detail: { variantId },
        }),
      );
      return sectionsHtml;
    } catch (error) {
      console.error("[henk-variant-switcher] section rendering mislukt", error);
      return null;
    } finally {
      if (ticket === this.sequence) this.removeAttribute("data-loading");
    }
  }
}

if (!customElements.get("henk-variant-switcher")) {
  customElements.define("henk-variant-switcher", HenkVariantSwitcher);
}
